import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, FolderOpen, MessagesSquare, ListTodo,
  CalendarDays, AlarmClock, User, LogOut, Bell, CreditCard, HandHelping, ClipboardList,
} from "lucide-react";
import Logo from "../components/Logo";
import { useAuth, isStaffUser } from "../context/AuthContext";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const MENU = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/intake", label: "My Intake", icon: ClipboardList },
  { to: "/portal/cases", label: "My Case", icon: Briefcase },
  { to: "/portal/services", label: "Request a Service", icon: HandHelping },
  { to: "/portal/documents", label: "Documents", icon: FolderOpen },
  { to: "/portal/messages", label: "Messages", icon: MessagesSquare },
  { to: "/portal/tasks", label: "Tasks", icon: ListTodo },
  { to: "/portal/appointments", label: "Appointments", icon: CalendarDays },
  { to: "/portal/deadlines", label: "Deadlines & Notices", icon: AlarmClock },
  { to: "/portal/payments", label: "Payments", icon: CreditCard },
  { to: "/portal/profile", label: "Profile", icon: User },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
  }, []);

  const unread = notifications.filter((n) => !n.read).length;

  const openNotifications = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch { /* silent */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col" data-testid="portal-layout">
      <header className="sticky top-0 z-40 bg-ivory/90 backdrop-blur-md border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="CF Full Circle home"><Logo size={36} /></Link>
          <div className="flex items-center gap-3">
            <Popover onOpenChange={(o) => o && unread > 0 && openNotifications()}>
              <PopoverTrigger asChild>
                <button className="relative p-2 text-navy hover:text-teal transition-colors" aria-label="Notifications" data-testid="notifications-button">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-champagne-dark text-white text-[10px] flex items-center justify-center" data-testid="notifications-badge">{unread}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 bg-surface border-line max-h-80 overflow-auto" data-testid="notifications-panel">
                <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal mb-2">Notifications</p>
                {notifications.length === 0 ? (
                  <p className="text-sm text-mutedink">No notifications yet.</p>
                ) : (
                  notifications.slice(0, 15).map((n) => (
                    <div key={n.notification_id} className={`py-2 border-b border-line last:border-0 text-sm ${n.read ? "text-mutedink" : "text-ink font-medium"}`}>
                      {n.text}
                      <span className="block text-[11px] text-mutedink mt-0.5">{new Date(n.created_at).toLocaleDateString()}</span>
                    </div>
                  ))
                )}
              </PopoverContent>
            </Popover>
            {isStaffUser(user) && (
              <Link to="/staff" className="text-xs font-semibold text-teal hover:text-teal-dark" data-testid="switch-to-staff-link">Staff View</Link>
            )}
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-mutedink hover:text-navy transition-colors" data-testid="portal-logout-button">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col lg:flex-row gap-8">
        <nav className="lg:w-56 flex-shrink-0" aria-label="Portal navigation">
          <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {MENU.map((m) => (
              <NavLink
                key={m.to}
                to={m.to}
                end={m.end}
                data-testid={`portal-nav-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-teal text-white" : "text-mutedink hover:bg-sand hover:text-white"}`
                }
              >
                <m.icon className="h-4 w-4" /> {m.label}
              </NavLink>
            ))}
          </div>
        </nav>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
